import {Inject, Injectable} from '@angular/core';
import {Resource} from 'ngrx-json-api';
import {filter, map, take} from 'rxjs/operators';
import {FilterByPipe} from 'ngx-pipes';
import {ProductService} from '../product-list-variant/product.service';
import {combineLatest, Observable, ReplaySubject} from 'rxjs';
import {SnipcartService} from '../../snipcart/snipcart.service';
import {CartProduct} from '../../snipcart/product/product.reducer';
import {CART_PRODUCTS_BY_SKU, ProductsSku} from '../../providers/cart.providers';
import {Snipcart} from '../../snipcart/snipcart.model';
import {Variant} from '../../model/product';
import {AppConfigService} from '../../config/app-config.service';

@Injectable({
  providedIn: 'root'
})
export class QuantityService {

  _variation: Variant;
  set variation(value: Variant) {
    this.variation$.next(value);
    this._variation = value;
  }

  variation$: ReplaySubject<Variant> = new ReplaySubject<Variant>(1);

  loading$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  cartProduct$: Observable<CartProduct> = combineLatest([this.variation$, this.cartProductsBySku$])
    .pipe(
      map(([variation, cartProducts]) => {
        if (variation?.sku && cartProducts) {
          return cartProducts[variation.sku];
        } else {
          return null;
        }
      })
    );

  hasVariantInCart$: Observable<boolean> = this.cartProduct$
    .pipe(
      map((cartProduct) => {
        const isAlreadyInCart: boolean = !!cartProduct;
        return isAlreadyInCart;
      })
    );

  snipcart: Snipcart;

  constructor(
    private filterByPipe: FilterByPipe,
    private productService: ProductService,
    private snipcartService: SnipcartService,
    @Inject(CART_PRODUCTS_BY_SKU) readonly cartProductsBySku$: Observable<ProductsSku>,
    protected appConfig: AppConfigService,
  ) {

    this.loading$.next(false);
    this.snipcartService.snipcart$.pipe(
      filter(snipcart => !!snipcart),
      take(1)
    ).subscribe(snipcart => {
      this.snipcart = snipcart;
    });
  }

  addVariantQuantity(quantity: number) {
    if (this._variation) {
      this.hasVariantInCart$
        .pipe(
          take(1)
        ).subscribe((hasVariantInCart) => {
        if (!hasVariantInCart) {
          this.loading$.next(true);
          this.addToSnipcart().then(result => {
              this.loading$.next(false);
            },
            error => {
              console.error('an error occured adding product', error);
              this.loading$.next(false);
            });
        }
      });

      this.checkCartProductForSnipcartUpdate(quantity);

    }
  }

  checkCartProductForSnipcartUpdate(quantity: number) {
    this.cartProduct$.pipe(take(1)).subscribe(cartProduct => {
      if (!!cartProduct) {
        this.triggerUpdateSnipcart(cartProduct.uuid, quantity);
      }
    });
  }

  triggerUpdateSnipcart(snipcartUuid: string, quantity: number) {
    this.loading$.next(true);
    this.updateSnipcart(snipcartUuid, quantity).then(result => {
        // console.log('successfully updated', result);
        this.loading$.next(false);
      },
      error => {
        console.error('an error occured adding or removing product', error);
        this.loading$.next(false);
      });
  }

  removeVariantQuantity(quantity: number) {
    this.cartProduct$.pipe(take(1)).subscribe(cartProduct => {
      if (cartProduct && !isNaN(quantity)) {

        if (quantity === 0) {
          this.loading$.next(true);
          this.removeFromSnipcart(cartProduct.uuid).then(result => {

            if (result?.code) {
              console.error('an error occured while removing product', result);
            } else {
              // console.log('successfully removed', result);
            }
            this.loading$.next(false);

          });
        } else if (quantity > 0) {
          this.checkCartProductForSnipcartUpdate(quantity);
        }

      }
    });

  }

  private addToDrupal(orderItem: Resource) {
    /*this.drupalCartService.getCartId$.pipe(take(1)).subscribe(cartId => {
      if (!!cartId) {
        if (orderItem) {
          const currentQuantity = parseInt(orderItem.attributes.quantity, 10);
          this.drupalCartService.updateCartItemQuantity(orderItem, currentQuantity + 1);
        } else {
          this.drupalCartService.addToCart(this._variation);
        }
      } else {
        this.drupalCartService.addToCart(this._variation);
      }
    });*/
  }

  private removeFromDrupal(orderItem: Resource) {
    /*const currentQuantity = parseInt(orderItem.attributes.quantity, 10);
    if (currentQuantity > 1) {
      this.drupalCartService.updateCartItemQuantity(orderItem, Math.max(0, currentQuantity - 1));
    } else {
      this.drupalCartService.removeFromCart(orderItem);
    }*/
  }

  private async addToSnipcart() {
    const snipcartItemUrl: string = this.appConfig.getConfig().backend + '/snipcart/product/' + this._variation.id;
    const unit: string = this._variation.unit;
    try {
      await this.snipcart.api.cart.items.add({
        id: this._variation.sku,
        name: this._variation.title + (unit ? ' - ' + unit : ''),
        description: this.productService.description,
        price: this._variation.price.number,
        url: snipcartItemUrl,
        taxable: !!this._variation.taxable,
        image: this.productService.snipcartImageUrl
      });
    } catch (error) {
      console.error(error);
    }
  }

  private async removeFromSnipcart(snipcartUuid: string) {
    try {
      await this.snipcart.api.cart.items.remove({
        uniqueId: snipcartUuid
      });

    } catch (error) {
      return error;
    }
  }

  private async updateSnipcart(snipcartUuid: string, quantity: number) {
    try {
      await this.snipcart.api.cart.items.update({
        uniqueId: snipcartUuid,
        quantity
      });
    } catch (error) {
      console.error(error);
    }
  }

}
